import React, { useState } from 'react';
import axios from 'axios';
import {
  XMarkIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { InvoiceData } from '../types/invoice';

interface InvoiceContext {
  invoiceId: number;
  filename: string | null;
  extractedDataSnapshot: InvoiceData | null;
}

interface Props {
  onClose: () => void;
  /** When set, modal opens in "report this invoice" mode. */
  invoiceContext: InvoiceContext | null;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const COMMON_ISSUES = [
  'Wrong vendor',
  'Wrong amount',
  'Wrong invoice number',
  'Wrong date',
  'Missing line items',
  'Wrong GL account',
  'PO did not match',
  'Other (see notes)',
];

const FeedbackModal: React.FC<Props> = ({ onClose, invoiceContext }) => {
  const { user } = useAuth();
  const isInvoiceMode = !!invoiceContext;

  const [rating, setRating] = useState<'good' | 'bad' | null>(
    isInvoiceMode ? 'bad' : null
  );
  const [subject, setSubject] = useState('');
  const [comment, setComment] = useState('');
  const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleIssue = (issue: string) =>
    setSelectedIssues((prev) =>
      prev.includes(issue) ? prev.filter((i) => i !== issue) : [...prev, issue]
    );

  const canSubmit = subject.trim().length > 0 || comment.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) {
      // Defensive guard — the submit button is disabled in this state so
      // users shouldn't normally see this toast.
      toast.error('Add a subject or a note before sending.');
      return;
    }
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API_BASE_URL}/feedback`,
        {
          // file_id / filename are only meaningful for invoice-mode feedback;
          // backend treats them as optional.
          file_id: invoiceContext ? `invoice-${invoiceContext.invoiceId}` : null,
          filename: invoiceContext?.filename ?? null,
          invoice_id: invoiceContext?.invoiceId ?? null,
          extracted_data_snapshot: invoiceContext?.extractedDataSnapshot ?? null,
          feedback: {
            rating,
            subject: subject.trim() || null,
            comment: comment.trim() || null,
            issues: selectedIssues.length > 0 ? selectedIssues : null,
            timestamp: new Date().toISOString(),
          },
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success('Thanks — feedback sent.');
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } };
      const msg = err?.response?.data?.detail || 'Could not send feedback. Try again.';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold dark:text-white">
              {isInvoiceMode ? 'Report Invoice Issue' : 'Send Feedback'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {user?.email || user?.username || ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Invoice context banner */}
          {invoiceContext && (
            <div className="flex items-start gap-3 p-3 rounded-md bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-800">
              <DocumentTextIcon className="h-5 w-5 text-brand-600 dark:text-brand-400 mt-0.5 shrink-0" />
              <div className="text-xs">
                <div className="font-medium text-brand-900 dark:text-brand-100">
                  Reporting this invoice
                </div>
                <div className="text-brand-700 dark:text-brand-300 mt-0.5">
                  {invoiceContext.filename || `Invoice #${invoiceContext.invoiceId}`}
                </div>
              </div>
            </div>
          )}

          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              How's it going?
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setRating('good')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm transition-colors ${
                  rating === 'good'
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:text-gray-300'
                }`}
              >
                <HandThumbUpIcon className="h-4 w-4" />
                Good
              </button>
              <button
                type="button"
                onClick={() => setRating('bad')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm transition-colors ${
                  rating === 'bad'
                    ? 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:text-gray-300'
                }`}
              >
                <HandThumbDownIcon className="h-4 w-4" />
                Bad
              </button>
            </div>
          </div>

          {/* Issues (only when reporting an invoice + rating is bad) */}
          {isInvoiceMode && rating === 'bad' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                What's wrong? (select all that apply)
              </label>
              <div className="grid grid-cols-2 gap-2">
                {COMMON_ISSUES.map((issue) => (
                  <button
                    key={issue}
                    type="button"
                    onClick={() => toggleIssue(issue)}
                    className={`text-left px-3 py-2 rounded-md border text-xs transition-colors ${
                      selectedIssues.includes(issue)
                        ? 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                        : 'border-gray-200 dark:border-gray-600 dark:text-gray-300 hover:border-gray-300'
                    }`}
                  >
                    {issue}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={isInvoiceMode ? "What's off about this invoice?" : 'One-line summary'}
              maxLength={200}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={isInvoiceMode
                ? 'Tell us more — e.g. "Vendor name is missing the suffix, and the total should be $1,243.55"'
                : 'Anything you want to share — bug, feature idea, confusion, etc.'}
              rows={5}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !canSubmit}
            title={!canSubmit ? 'Add a subject or a note first' : undefined}
            className="px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Sending…' : 'Send Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
